import { createTheme } from '@mui/material/styles';

// Neon Dark Color Palette
const colors = {
  // Primary colors - Neon Cyan
  primary: {
    main: '#22d3ee',
    light: '#67e8f9',
    dark: '#06b6d4',
    contrastText: '#0f172a',
  },
  // Secondary colors - Neon Magenta
  secondary: {
    main: '#e879f9',
    light: '#f0abfc',
    dark: '#d946ef',
    contrastText: '#0f172a',
  },
  // Success colors - Neon Green
  success: {
    main: '#34d399',
    light: '#6ee7b7',
    dark: '#10b981',
    contrastText: '#0f172a',
  },
  // Warning colors - Neon Yellow
  warning: {
    main: '#facc15',
    light: '#fde047',
    dark: '#eab308',
    contrastText: '#0f172a',
  },
  // Error colors - Neon Red
  error: {
    main: '#f43f5e',
    light: '#fb7185',
    dark: '#e11d48',
    contrastText: '#0f172a',
  },
  // Info colors - Neon Purple
  info: {
    main: '#a78bfa',
    light: '#c4b5fd',
    dark: '#8b5cf6',
    contrastText: '#0f172a',
  },
  // Background shades - Pure Dark
  background: {
    default: '#09090b',
    paper: '#18181b',
    card: '#1e1e22',
    elevated: '#27272a',
  },
  // Text colors
  text: {
    primary: '#fafafa',
    secondary: '#a1a1aa',
    disabled: '#71717a',
  },
  // Border and divider colors
  divider: 'rgba(255, 255, 255, 0.08)',
  border: 'rgba(255, 255, 255, 0.12)',
  // Neon glow colors (for box-shadows)
  glow: {
    cyan: 'rgba(34, 211, 238, 0.3)',
    magenta: 'rgba(232, 121, 249, 0.3)',
    green: 'rgba(52, 211, 153, 0.3)',
    yellow: 'rgba(250, 204, 21, 0.3)',
    red: 'rgba(244, 63, 94, 0.3)',
    purple: 'rgba(167, 139, 250, 0.3)',
  },
  // Gradient backgrounds
  gradients: {
    primary: 'linear-gradient(120deg, #06b6d4 0%, #22d3ee 100%)',
    secondary: 'linear-gradient(120deg, #d946ef 0%, #e879f9 100%)',
    success: 'linear-gradient(120deg, #10b981 0%, #34d399 100%)',
    info: 'linear-gradient(120deg, #8b5cf6 0%, #a78bfa 100%)',
    darkGlass: 'linear-gradient(135deg, rgba(24, 24, 27, 0.8) 0%, rgba(9, 9, 11, 0.9) 100%)',
    mainBackground: 'radial-gradient(circle at 10% 20%, rgb(9, 9, 11) 0%, rgb(14, 14, 16) 90.2%)',
  },
};

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: colors.primary,
    secondary: colors.secondary,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
    divider: colors.divider,
    gradients: colors.gradients,
    glow: colors.glow,
  },

  typography: {
    fontFamily: '"JetBrains Mono", "Roboto Mono", "Courier New", monospace',
    h1: {
      fontWeight: 700,
      fontSize: '2rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.1rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    body1: {
      fontSize: '0.95rem',
    },
    body2: {
      fontSize: '0.85rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },

  shape: {
    borderRadius: 12,
  },

  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.2)',
    '0px 4px 8px rgba(0, 0, 0, 0.2)',
    '0px 6px 12px rgba(0, 0, 0, 0.2)',
    '0px 8px 16px rgba(0, 0, 0, 0.2)',
    '0px 10px 20px rgba(0, 0, 0, 0.2)',
    '0px 12px 24px rgba(0, 0, 0, 0.2)',
    '0px 14px 28px rgba(0, 0, 0, 0.2)',
    '0px 16px 32px rgba(0, 0, 0, 0.2)',
    '0px 18px 36px rgba(0, 0, 0, 0.2)',
    '0px 20px 40px rgba(0, 0, 0, 0.2)',
    '0px 22px 44px rgba(0, 0, 0, 0.2)',
    '0px 24px 48px rgba(0, 0, 0, 0.2)',
    '0px 26px 52px rgba(0, 0, 0, 0.2)',
    '0px 28px 56px rgba(0, 0, 0, 0.2)',
    '0px 30px 60px rgba(0, 0, 0, 0.2)',
    '0px 32px 64px rgba(0, 0, 0, 0.2)',
    '0px 34px 68px rgba(0, 0, 0, 0.2)',
    '0px 36px 72px rgba(0, 0, 0, 0.2)',
    '0px 38px 76px rgba(0, 0, 0, 0.2)',
    '0px 40px 80px rgba(0, 0, 0, 0.2)',
    '0px 42px 84px rgba(0, 0, 0, 0.2)',
    '0px 44px 88px rgba(0, 0, 0, 0.2)',
    '0px 46px 92px rgba(0, 0, 0, 0.2)',
    '0px 48px 96px rgba(0, 0, 0, 0.2)',
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: `${colors.divider} transparent`,
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: colors.divider,
            borderRadius: '4px',
            '&:hover': {
              background: colors.text.disabled,
            },
          },
          background: colors.gradients.mainBackground,
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          backgroundImage: 'none',
          transition: 'all 0.2s ease-in-out',
        },
        elevation1: {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          backgroundColor: colors.background.card,
        },
        elevation2: {
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.18)',
          backgroundColor: colors.background.elevated,
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 12,
          padding: '10px 18px',
          transition: 'all 0.2s ease-in-out',
        },
        contained: {
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          '&:hover': {
            boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.2)',
            transform: 'translateY(-2px)',
          },
          '&:active': {
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(1px)',
          },
        },
        containedPrimary: {
          background: `linear-gradient(45deg, ${colors.primary.dark} 0%, ${colors.primary.main} 100%)`,
          '&:hover': {
            background: `linear-gradient(45deg, ${colors.primary.dark} 20%, ${colors.primary.main} 100%)`,
          },
        },
        containedSecondary: {
          background: `linear-gradient(45deg, ${colors.secondary.dark} 0%, ${colors.secondary.main} 100%)`,
          '&:hover': {
            background: `linear-gradient(45deg, ${colors.secondary.dark} 20%, ${colors.secondary.main} 100%)`,
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            transform: 'translateY(-2px)',
          },
        },
        outlinedPrimary: {
          borderColor: colors.primary.main,
          '&:hover': {
            backgroundColor: 'rgba(34, 211, 238, 0.08)',
            boxShadow: `0 0 15px ${colors.glow.cyan}`,
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s',
          borderRadius: 10,
          padding: 10,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.paper,
          backgroundImage: 'none',
          borderRight: `1px solid ${colors.divider}`,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: colors.background.paper,
          boxShadow: '0px 10px 40px rgba(0, 0, 0, 0.3)',
          borderRadius: 16,
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: colors.background.card,
          borderRadius: 16,
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          height: 8,
          backgroundColor: 'rgba(34, 211, 238, 0.1)',
        },
        bar: {
          borderRadius: 8,
          background: `linear-gradient(90deg, ${colors.primary.dark} 0%, ${colors.primary.main} 100%)`,
          boxShadow: `0 0 10px ${colors.glow.cyan}`,
        },
      },
    },

    MuiTable: {
      styleOverrides: {
        root: {
          tableLayout: 'fixed',
          borderCollapse: 'separate',
          borderSpacing: '0 6px',
          backgroundColor: 'transparent',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'all 0.3s',
          backgroundColor: colors.background.card,
          '&:hover': {
            backgroundColor: colors.background.elevated,
            transform: 'translateY(-2px)',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(34, 211, 238, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(34, 211, 238, 0.12)',
            },
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
          padding: '14px 16px',
          color: colors.text.primary,
          '&:first-of-type': {
            borderTopLeftRadius: 12,
            borderBottomLeftRadius: 12,
          },
          '&:last-of-type': {
            borderTopRightRadius: 12,
            borderBottomRightRadius: 12,
          },
        },
        head: {
          backgroundColor: 'transparent',
          color: colors.text.secondary,
          fontWeight: 600,
          fontSize: '0.85rem',
          lineHeight: '1.5',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
        },
      },
    },

    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 10,
          color: colors.text.secondary,
          borderColor: colors.divider,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&.Mui-selected': {
            backgroundColor: 'rgba(34, 211, 238, 0.1)',
            color: colors.primary.main,
            borderColor: 'rgba(34, 211, 238, 0.3)',
            boxShadow: `0 0 15px ${colors.glow.cyan}`,
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '& fieldset': {
              borderColor: colors.divider,
              borderWidth: '1.5px',
              transition: 'all 0.2s',
            },
            '&:hover fieldset': {
              borderColor: colors.text.secondary,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main,
              borderWidth: '2px',
            },
          },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '12px 16px',
          borderWidth: '1px',
          borderStyle: 'solid',
        },
        standardSuccess: {
          backgroundColor: 'rgba(52, 211, 153, 0.1)',
          color: colors.success.light,
          borderColor: 'rgba(52, 211, 153, 0.3)',
        },
        standardError: {
          backgroundColor: 'rgba(244, 63, 94, 0.1)',
          color: colors.error.light,
          borderColor: 'rgba(244, 63, 94, 0.3)',
        },
        standardWarning: {
          backgroundColor: 'rgba(250, 204, 21, 0.1)',
          color: colors.warning.light,
          borderColor: 'rgba(250, 204, 21, 0.3)',
        },
        standardInfo: {
          backgroundColor: 'rgba(34, 211, 238, 0.1)',
          color: colors.primary.light,
          borderColor: 'rgba(34, 211, 238, 0.3)',
        },
      },
    },
    
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'all 0.2s ease',
          borderWidth: '1px',
          borderStyle: 'solid',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
        colorPrimary: {
          backgroundColor: 'rgba(34, 211, 238, 0.1)',
          color: colors.primary.light,
          borderColor: 'rgba(34, 211, 238, 0.3)',
          '&:hover': {
            backgroundColor: 'rgba(34, 211, 238, 0.2)',
            boxShadow: `0 0 10px ${colors.glow.cyan}`,
          },
        },
        colorSecondary: {
          backgroundColor: 'rgba(232, 121, 249, 0.1)',
          color: colors.secondary.light,
          borderColor: 'rgba(232, 121, 249, 0.3)',
          '&:hover': {
            backgroundColor: 'rgba(232, 121, 249, 0.2)',
            boxShadow: `0 0 10px ${colors.glow.magenta}`,
          },
        },
        colorSuccess: {
          backgroundColor: 'rgba(52, 211, 153, 0.1)',
          color: colors.success.light,
          borderColor: 'rgba(52, 211, 153, 0.3)',
          '&:hover': {
            backgroundColor: 'rgba(52, 211, 153, 0.2)',
            boxShadow: `0 0 10px ${colors.glow.green}`,
          },
        },
        colorWarning: {
          backgroundColor: 'rgba(250, 204, 21, 0.1)',
          color: colors.warning.light,
          borderColor: 'rgba(250, 204, 21, 0.3)',
          '&:hover': {
            backgroundColor: 'rgba(250, 204, 21, 0.2)',
            boxShadow: `0 0 10px ${colors.glow.yellow}`,
          },
        },
        colorError: {
          backgroundColor: 'rgba(244, 63, 94, 0.1)',
          color: colors.error.light,
          borderColor: 'rgba(244, 63, 94, 0.3)',
          '&:hover': {
            backgroundColor: 'rgba(244, 63, 94, 0.2)',
            boxShadow: `0 0 10px ${colors.glow.red}`,
          },
        },
        colorInfo: {
          backgroundColor: 'rgba(167, 139, 250, 0.1)',
          color: colors.info.light,
          borderColor: 'rgba(167, 139, 250, 0.3)',
          '&:hover': {
            backgroundColor: 'rgba(167, 139, 250, 0.2)',
            boxShadow: `0 0 10px ${colors.glow.purple}`,
          },
        },
      },
    },
  },
});
